import React from 'react';
import { splitSsn } from 'utils/strings';
import { formatNumber } from 'utils/number';
import { usePortfolioTranslation as useLocale } from 'jsx/hooks/useTranslation';
import { CIP_COLOR_MAPPING, DD_REGISTRY_MAPPING } from 'constants/portfolio';
import cancelGray from './cancel-gray.svg';
import cancelRed from './cancel-red.svg';

const PortfolioTableRow = React.memo(({ customer }) => {
  const {
    ddSeverityMax,
    isMonitoredDD,
    isMonitoredCIP,
    firmRegistryLink,
    currentCIPScore,
    currentOnDDRegistry,
    isCurrent,
    subjectName,
    subjectRegno,
    companyIsatImpactScore,
  } = customer;

  const { t } = useLocale();
  let bellTip = t('portfolio_table_isNotMonitored');
  let alertTip = t(DD_REGISTRY_MAPPING[ddSeverityMax]);
  const bell = isMonitoredDD || isMonitoredCIP ? 'text-blue' : 'text-gray';
  const firmRegLink = `${process.env.REACT_APP_BUSINESS_WEB_URL}/Search/Search.aspx?${firmRegistryLink}`;
  if (isMonitoredCIP) {
    bellTip = isMonitoredDD
      ? t('portfolio_table_isMonitored')
      : t('portfolio_table_isMonitoredCIP');
  } else if (isMonitoredDD) {
    bellTip = t('portfolio_table_isMonitoredDD');
  }
  const alertDD = ddSeverityMax > 0 && ddSeverityMax < 4 ? 'icon-alert text-28 text-red' : '';

  const notCalculated = (
    <span data-for="credit" data-tip={t('portfolio_table_cipNotCalculated')}>
      <img src={cancelGray} alt={t('portfolio_table_cipNotCalculated')} />
    </span>
  );

  let tagSpan = <span>-</span>;

  if (!isMonitoredCIP) {
    tagSpan = notCalculated;
  } else {
    if (currentCIPScore) {
      const cip = CIP_COLOR_MAPPING[currentCIPScore.trim()];
      if (cip !== undefined) {
        tagSpan = (
          <span style={{ marginLeft: 0 }} className={`tag ${cip}`}>
            {currentCIPScore}
          </span>
        );
      } else {
        tagSpan = notCalculated;
      }
    } else {
      tagSpan = notCalculated;
    }
    if (ddSeverityMax > 0 && currentOnDDRegistry) {
      tagSpan = (
        <span data-for="credit" data-tip={t('portfolio_table_cipNotCalculated')}>
          <img src={cancelRed} alt={t('portfolio_table_cipNotCalculated')} />
        </span>
      );
    }
  }

  let subText = '';
  if (!isCurrent) {
    subText =
      customer.subjectRegno < '4'
        ? t('portfolio_table_notCurrent_individual')
        : t('portfolio_table_notCurrent_company');
  }
  return (
    <tr>
      <td style={{ verticalAlign: 'middle' }}>
        <span data-for="isMonitored" data-tip={bellTip} className={`text-32 icon-bell2 ${bell}`} />
      </td>
      <td>
        <div>
          <a href={firmRegLink} target="_blank" rel="noopener noreferrer">
            {subjectName} {subText}
          </a>
        </div>
        <div>{splitSsn(subjectRegno)}</div>
      </td>
      <td className="text-center">
        {tagSpan}{' '}
        <span
          data-for="isRegisteredDD"
          style={{ position: 'absolute', paddingLeft: '10px' }}
          data-tip={alertTip}
          className={alertDD}
        />
      </td>
      <td>
        <span
          data-for="covidDescription"
          data-tip={t(
            companyIsatImpactScore
              ? `covid_impact_title_tooltip_${companyIsatImpactScore}`
              : 'covid_impact_title_tooltip'
          )}
        >
          {t(
            companyIsatImpactScore
              ? `covid_impact_title_${companyIsatImpactScore}`
              : 'covid_impact_title'
          )}
        </span>
      </td>
      <td>{ifPBS(t, PaymentBehavior, customer)}</td>
      <td className="text-left">{ifPBS(t, OutstandingInvoices, customer)}</td>
      <td className="text-right">{ifPBS(t, OutstandingDebt, customer)}</td>
    </tr>
  );
});

const PaymentBehavior = ({ customer }) => {
  const { t } = useLocale();
  let str = t('portfolio_table_pbs_noData');
  if (customer.pbsPaymentbehavior6m !== null) {
    if (customer.pbsPaymentbehavior6m === 0) {
      str = t('portfolio_table_pbehave_on');
    } else {
      const after = customer.pbsPaymentbehavior6m > 0;
      str = t(
        `portfolio_table_pbehave_${after ? 'after' : 'before'}`,
        Math.abs(customer.pbsPaymentbehavior6m)
      );
    }
  }
  return <div dangerouslySetInnerHTML={{ __html: str }} />;
};

const ifPBS = (t, Component, customer) => {
  if (!customer.isPBS) return t('portfolio_table_pbs_noData');
  if (customer.isPBSExcluded) return t('portfolio_table_pbs_excluded');
  return <Component customer={customer} />;
};

const OutstandingDebt = ({ customer }) => {
  const { pbsOutstandingAmountAll, pbsOutstandingAmountPastDue } = customer;

  return (
    <>
      <div>{formatNumber(pbsOutstandingAmountAll)} kr.</div>
      {pbsOutstandingAmountPastDue > 0 && (
        <div className="text-red">({formatNumber(pbsOutstandingAmountPastDue)} kr.)</div>
      )}
    </>
  );
};

const OutstandingInvoices = ({ customer }) => {
  return customer.pbsOutstandingCountAll;
};

export default PortfolioTableRow;
